import { useState, useEffect } from 'react';
import { api } from '@shared/lib/api';
import SaveBar from './SaveBar';
import type { Merchant } from '@shared/types';

interface StripeConnectTabProps {
  merchant: Merchant | null;
  statementDescriptor: string;
  setStatementDescriptor: (v: string) => void;
  saving: boolean;
  saveSuccess: boolean;
  saveError: string;
  onSave: () => void;
}

interface StripeStatus {
  connected: boolean;
  stripe_account_id: string | null;
  charges_enabled: boolean;
  payouts_enabled: boolean;
  details_submitted: boolean;
}

function InfoBubble({ text }: { text: string }) {
  return (
    <span className="group relative inline-flex ml-1.5 cursor-help">
      <span
        className="w-3.5 h-3.5 rounded-full flex items-center justify-center text-[9px] font-mono transition-colors"
        style={{ border: '1px solid var(--dash-active-border)', color: 'var(--dash-text-muted)' }}
      >
        ?
      </span>
      <span
        className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-64 rounded px-3 py-2 font-sans text-[11px] leading-relaxed opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-150 shadow-xl z-50 pointer-events-none"
        style={{ background: 'var(--dash-overlay)', border: '1px solid var(--dash-border)', color: 'var(--dash-text-tertiary)' }}
      >
        {text}
        <span className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent" style={{ borderTopColor: 'var(--dash-border)' }} />
      </span>
    </span>
  );
}

export default function StripeConnectTab({ merchant, statementDescriptor, setStatementDescriptor, saving, saveSuccess, saveError, onSave }: StripeConnectTabProps) {
  const [stripeLoading, setStripeLoading] = useState(false);
  const [stripeError, setStripeError] = useState('');
  const [stripeIdCopied, setStripeIdCopied] = useState(false);
  const [pkCopied, setPkCopied] = useState(false);
  const [pkVisible, setPkVisible] = useState(false);
  const [publishableKey, setPublishableKey] = useState('');
  const [statusLoading, setStatusLoading] = useState(true);
  const [stripeStatus, setStripeStatus] = useState<StripeStatus | null>(null);
  const [focusedField, setFocusedField] = useState<string | null>(null);
  const [hoveredBtn, setHoveredBtn] = useState<string | null>(null);
  const [copyIdHover, setCopyIdHover] = useState(false);
  const [stripeLinkHover, setStripeLinkHover] = useState(false);

  const maskKey = (key: string) => {
    if (key.length <= 12) return key;
    return key.slice(0, 8) + '...' + key.slice(-4);
  };

  useEffect(() => {
    api.getStripePublishableKey()
      .then(({ publishable_key }) => setPublishableKey(publishable_key))
      .catch(() => {});

    api.stripeConnectStatus()
      .then((status) => {
        setStripeStatus(status);
        setStatusLoading(false);
      })
      .catch(() => {
        setStatusLoading(false);
      });
  }, []);

  const isOnboarded = merchant?.stripe_onboarded || (stripeStatus?.charges_enabled && stripeStatus?.details_submitted);
  const stripeAccountId = stripeStatus?.stripe_account_id || merchant?.stripe_account_id;
  const isConnected = stripeStatus?.connected || !!stripeAccountId;
  const isPending = isConnected && !isOnboarded;

  const handleStripeConnect = async () => {
    setStripeLoading(true);
    setStripeError('');
    try {
      const returnUrl = `${window.location.origin}/dashboard/settings`;
      const refreshUrl = `${window.location.origin}/dashboard/settings`;
      const result = await api.stripeConnectOnboard(returnUrl, refreshUrl);
      window.location.href = result.onboarding_url;
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to start Stripe onboarding';
      setStripeError(message);
      setStripeLoading(false);
    }
  };

  const inputClass = 'w-full rounded px-3 py-2 font-sans text-sm focus:outline-none transition-colors';

  const inputStyle: React.CSSProperties = {
    border: '1px solid var(--dash-border)',
    background: 'var(--dash-input)',
    color: 'var(--dash-text-secondary)',
  };

  const inputFocusStyle: React.CSSProperties = {
    ...inputStyle,
    borderColor: 'var(--dash-active-border)',
  };

  const getInputStyle = (field: string) =>
    focusedField === field ? inputFocusStyle : inputStyle;

  const actionBtnStyle = (id: string): React.CSSProperties => ({
    color: hoveredBtn === id ? 'var(--dash-text-tertiary)' : 'var(--dash-text-muted)',
    border: hoveredBtn === id ? '1px solid var(--dash-border)' : '1px solid var(--dash-border)',
  });

  if (statusLoading) {
    return (
      <div className="rounded p-5" style={{ border: '1px solid var(--dash-border)', background: 'var(--dash-card)' }}>
        <h2 className="font-mono text-[10px] uppercase tracking-widest mb-3" style={{ color: 'var(--dash-text-muted)' }}>Stripe Connect</h2>
        <p className="font-sans text-sm" style={{ color: 'var(--dash-text-muted)' }}>Loading Stripe status...</p>
      </div>
    );
  }

  return (
    <>
    <div className="rounded p-5" style={{ border: '1px solid var(--dash-border)', background: 'var(--dash-card)' }}>
      <div className="flex items-center mb-2">
        <h2 className="font-mono text-[10px] uppercase tracking-widest" style={{ color: 'var(--dash-text-muted)' }}>Stripe Connect</h2>
        <InfoBubble text="Stripe Connect allows you to accept card payments through BonesPay. Once connected, customers can pay with credit/debit cards and funds are deposited directly to your Stripe account." />
      </div>
      <p className="font-sans text-[12px] leading-relaxed mb-5" style={{ color: 'var(--dash-text-muted)' }}>
        Connect your Stripe account to accept card payments alongside crypto. Stripe handles PCI compliance, disputes, and payouts.
      </p>

      <div className="space-y-4">
        <div>
          <div className="flex items-center mb-1.5">
            <label className="font-mono text-[10px] uppercase tracking-widest" style={{ color: 'var(--dash-text-muted)' }}>Status</label>
            <InfoBubble text="Shows your current Stripe integration status. 'Active' means you can accept payments. 'Pending' means onboarding is incomplete. 'Not Connected' means you haven't started the setup process." />
          </div>
          {isOnboarded ? (
            <span className="inline-block font-mono text-[10px] uppercase tracking-widest px-2 py-0.5 rounded border text-emerald-400 bg-emerald-400/10 border-emerald-400/20">Active</span>
          ) : isPending ? (
            <span className="inline-block font-mono text-[10px] uppercase tracking-widest px-2 py-0.5 rounded border text-amber-400 bg-amber-400/10 border-amber-400/20">Pending</span>
          ) : (
            <span
              className="inline-block font-mono text-[10px] uppercase tracking-widest px-2 py-0.5 rounded"
              style={{ color: 'var(--dash-text-muted)', background: 'var(--dash-hover-bg)', border: '1px solid var(--dash-border-light)' }}
            >
              Not Connected
            </span>
          )}
        </div>

        {stripeAccountId && (
          <div>
            <div className="flex items-center mb-1.5">
              <label className="font-mono text-[10px] uppercase tracking-widest" style={{ color: 'var(--dash-text-muted)' }}>Account ID</label>
              <InfoBubble text="Your Stripe Connect account identifier. This is automatically created when you begin onboarding and links your BonesPay merchant to your Stripe account." />
            </div>
            <div className="flex items-center gap-2">
              <code
                className="flex-1 font-mono text-xs rounded px-3 py-2"
                style={{ color: 'var(--dash-text-muted)', background: 'var(--dash-input)', border: '1px solid var(--dash-border)' }}
              >
                {maskKey(stripeAccountId)}
              </code>
              <button
                onClick={() => {
                  navigator.clipboard.writeText(stripeAccountId);
                  setStripeIdCopied(true);
                  setTimeout(() => setStripeIdCopied(false), 2000);
                }}
                className="transition-colors p-1 font-mono text-sm"
                style={{ color: copyIdHover ? 'var(--dash-text-tertiary)' : 'var(--dash-text-muted)' }}
                onMouseEnter={() => setCopyIdHover(true)}
                onMouseLeave={() => setCopyIdHover(false)}
              >
                {stripeIdCopied ? '\u2713' : '\u29C9'}
              </button>
            </div>
          </div>
        )}

        {stripeStatus && isConnected && !isOnboarded && (
          <div className="rounded p-4" style={{ border: '1px solid var(--dash-border-light)', background: 'var(--dash-input)' }}>
            <p className="font-mono text-[10px] uppercase tracking-widest mb-3" style={{ color: 'var(--dash-text-muted)' }}>Onboarding Checklist</p>
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <span className={`font-mono text-xs ${stripeStatus.details_submitted ? 'text-emerald-400' : ''}`} style={!stripeStatus.details_submitted ? { color: 'var(--dash-text-faint)' } : undefined}>
                  {stripeStatus.details_submitted ? '\u2713' : '\u25CB'}
                </span>
                <span className="font-sans text-[12px]" style={{ color: stripeStatus.details_submitted ? 'var(--dash-text-tertiary)' : 'var(--dash-text-muted)' }}>
                  Business details submitted
                </span>
              </div>
              <div className="flex items-center gap-2">
                <span className={`font-mono text-xs ${stripeStatus.charges_enabled ? 'text-emerald-400' : ''}`} style={!stripeStatus.charges_enabled ? { color: 'var(--dash-text-faint)' } : undefined}>
                  {stripeStatus.charges_enabled ? '\u2713' : '\u25CB'}
                </span>
                <span className="font-sans text-[12px]" style={{ color: stripeStatus.charges_enabled ? 'var(--dash-text-tertiary)' : 'var(--dash-text-muted)' }}>
                  Card payments enabled
                </span>
              </div>
              <div className="flex items-center gap-2">
                <span className={`font-mono text-xs ${stripeStatus.payouts_enabled ? 'text-emerald-400' : ''}`} style={!stripeStatus.payouts_enabled ? { color: 'var(--dash-text-faint)' } : undefined}>
                  {stripeStatus.payouts_enabled ? '\u2713' : '\u25CB'}
                </span>
                <span className="font-sans text-[12px]" style={{ color: stripeStatus.payouts_enabled ? 'var(--dash-text-tertiary)' : 'var(--dash-text-muted)' }}>
                  Payouts enabled
                </span>
              </div>
            </div>
          </div>
        )}

        {isOnboarded ? (
          <div className="space-y-4">
            {publishableKey && (
              <div className="rounded p-4" style={{ border: '1px solid var(--dash-border-light)', background: 'var(--dash-input)' }}>
                <div className="flex items-center mb-2">
                  <label className="font-mono text-[10px] uppercase tracking-widest" style={{ color: 'var(--dash-text-muted)' }}>Publishable Key</label>
                  <InfoBubble text="Your Stripe publishable key is required for inline card payments in your WordPress plugin or any client-side Stripe integration. This key is safe to use in frontend code -- it can only create tokens, not charge cards." />
                </div>
                <p className="font-sans text-[11px] mb-3" style={{ color: 'var(--dash-text-faint)' }}>
                  Use this key in your <span style={{ color: 'var(--dash-text-muted)' }}>WordPress plugin settings</span> to enable inline card payments on your site.
                </p>
                <div className="flex items-center gap-2">
                  <code
                    className="flex-1 font-mono text-xs rounded px-3 py-2 break-all"
                    style={{ color: 'var(--dash-text-muted)', background: 'var(--dash-card)', border: '1px solid var(--dash-border)' }}
                  >
                    {pkVisible ? publishableKey : maskKey(publishableKey)}
                  </code>
                  <button
                    onClick={() => setPkVisible(!pkVisible)}
                    className="transition-colors p-1.5 font-mono text-[11px] rounded"
                    style={actionBtnStyle('pkToggle')}
                    onMouseEnter={() => setHoveredBtn('pkToggle')}
                    onMouseLeave={() => setHoveredBtn(null)}
                    title={pkVisible ? 'Hide' : 'Reveal'}
                  >
                    {pkVisible ? '\u25C9' : '\u25CB'}
                  </button>
                  <button
                    onClick={() => {
                      navigator.clipboard.writeText(publishableKey);
                      setPkCopied(true);
                      setTimeout(() => setPkCopied(false), 2000);
                    }}
                    className="transition-colors p-1.5 font-mono text-[11px] rounded"
                    style={actionBtnStyle('pkCopy')}
                    onMouseEnter={() => setHoveredBtn('pkCopy')}
                    onMouseLeave={() => setHoveredBtn(null)}
                    title="Copy"
                  >
                    {pkCopied ? '\u2713' : '\u29C9'}
                  </button>
                </div>
              </div>
            )}
            <a
              href="https://dashboard.stripe.com"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 font-mono text-xs transition-colors"
              style={{ color: stripeLinkHover ? 'var(--dash-text-primary)' : 'var(--dash-text-tertiary)' }}
              onMouseEnter={() => setStripeLinkHover(true)}
              onMouseLeave={() => setStripeLinkHover(false)}
            >
              Open Stripe Dashboard <span className="text-[10px]">{'\u2197'}</span>
            </a>
          </div>
        ) : (
          <>
            {stripeError && (
              <div className="border border-red-500/30 bg-red-500/10 rounded px-4 py-3">
                <p className="font-sans text-sm text-red-300">{stripeError}</p>
              </div>
            )}
            <button
              onClick={handleStripeConnect}
              disabled={stripeLoading}
              className="inline-flex items-center gap-2 bg-blue-600 text-white font-mono text-sm font-medium px-5 py-2 rounded hover:bg-blue-500 transition-colors disabled:opacity-50 uppercase tracking-wider group"
            >
              {stripeLoading ? (
                <><span className="inline-block animate-spin text-sm">{'\u25CC'}</span> Connecting...</>
              ) : (
                <>{isPending ? 'Complete Onboarding' : 'Connect with Stripe'} <span className="group-hover:translate-x-0.5 transition-transform text-sm">{'\u2192'}</span></>
              )}
            </button>
          </>
        )}
      </div>
    </div>

    {isOnboarded && merchant && (
      <div className="rounded p-5 mt-5" style={{ border: '1px solid var(--dash-border)', background: 'var(--dash-card)' }}>
        <div className="flex items-center mb-2">
          <h2 className="font-mono text-[10px] uppercase tracking-widest" style={{ color: 'var(--dash-text-muted)' }}>Statement Descriptor</h2>
          <InfoBubble text="The statement descriptor appears on your customer's bank or card statement when they make a payment. Keep it recognizable so customers can identify the charge. Maximum 22 characters, letters, numbers, and spaces only." />
        </div>
        <p className="font-sans text-[12px] leading-relaxed mb-4" style={{ color: 'var(--dash-text-muted)' }}>
          This text appears on your customer's bank or card statement to help them identify the charge.
        </p>
        <div className="space-y-4">
          <div>
            <label className="block font-mono text-[10px] uppercase tracking-widest mb-1.5" style={{ color: 'var(--dash-text-muted)' }}>
              Descriptor Text
            </label>
            <input
              type="text"
              value={statementDescriptor}
              onChange={(e) => {
                const val = e.target.value.slice(0, 22);
                setStatementDescriptor(val);
              }}
              placeholder="e.g. MYSTORE"
              className={inputClass}
              style={getInputStyle('descriptor')}
              onFocus={() => setFocusedField('descriptor')}
              onBlur={() => setFocusedField(null)}
            />
            <div className="flex items-center justify-between mt-1.5">
              <p className="font-sans text-[10px]" style={{ color: 'var(--dash-text-faint)' }}>
                Letters, numbers, and spaces only. Must be recognizable to customers.
              </p>
              <span className="font-mono text-[10px]" style={{ color: 'var(--dash-text-faint)' }}>{statementDescriptor.length}/22</span>
            </div>
          </div>
          <SaveBar saving={saving} saveSuccess={saveSuccess} saveError={saveError} onSave={onSave} />
        </div>
      </div>
    )}
    </>
  );
}
